/* eslint-disable no-mixed-spaces-and-tabs */
import { useEffect, useState } from "react";
import { t } from "i18next";
import Feature from "proximiio-js-library/lib/models/feature";
import { FaFlag, FaPersonWalking, FaArrowRotateRight } from "react-icons/fa6";
import { Button } from "./ui/button";
import { cn, getFloorName } from "@/lib/utils";

import useMapStore from "@/store/mapStore";

export interface StepModel {
  bearingFromLastStep: number;
  coordinates: { coordinates: [number, number] };
  direction: string;
  distanceFromLastStep: number;
  instruction?: string[];
  isWaypoint: boolean;
  level: number;
  levelChangerId: string;
  lineStringFeatureFromLastStep: Feature;
  waypointId: string;
  destinationLevel?: number;
  stepsUntil?: StepModel[];
  totalDistance?: number;
  description: string;
}

function PoiDetailsSteps() {
  const [steps, setSteps] = useState([] as StepModel[]);

  const map = useMapStore((state) => state.map);
  const haveRouteDetails = useMapStore((state) => state.haveRouteDetails);
  const routeDetails = useMapStore((state) => state.routeDetails);
  const floors = useMapStore((state) => state.floors);
  const currentFloor = useMapStore((state) => state.currentFloor);
  const currentLanguage = useMapStore((state) => state.currentLang);
  const currentStep = useMapStore((state) => state.currentStep);
  const mobileMode = useMapStore((state) => state.mobileMode);
  const setCurrentStep = useMapStore((state) => state.setCurrentStep);
  const setStepAnimated = useMapStore((state) => state.setStepAnimated);
  const resetAnimatedSteps = useMapStore((state) => state.resetAnimatedSteps);

  useEffect(() => {
    if (haveRouteDetails) {
      const textNavSteps: StepModel[] = routeDetails.TBTNav.steps;
      let previousIndex = 0;
      setSteps(
        textNavSteps
          .filter((i, index, array) => {
            // Get the first part of the direction string
            const direction = i.direction.split("_")[0];
            // Check if the current step is a level changer and has a valid direction or is finish
            if (
              (i.levelChangerId &&
                (direction === "UP" || direction === "DOWN")) ||
              i.direction === "FINISH"
            ) {
              i.stepsUntil = array.slice(previousIndex, index);
              previousIndex = index + 1;
              return i;
            }
          })
          .map((step) => {
            const direction = step.direction.split("_")[0]
              ? step.direction.split("_")[0]
              : step.direction;
            const levelChangerType = step.direction.split("_")[1];
            const destinationFloor = floors.filter(
              (f) => f.level === step.destinationLevel,
            )
              ? floors.filter((f) => f.level === step.destinationLevel)[0]
              : currentFloor;

            const stepsUntilDistance = step.stepsUntil!.reduce(
              (total, item) => total + item.distanceFromLastStep,
              0,
            );
            const totalDistance =
              step.distanceFromLastStep + stepsUntilDistance;

            let description;
            if (direction === "FINISH") {
              description =
                currentLanguage === "fi"
                  ? `${t("you-will-arrive")} ${totalDistance | 0}m ${t("in")}.`
                  : `${t("in")} ${totalDistance | 0}m ${t("you-will-arrive")}.`;
            } else {
              description = `${t("go")} ${totalDistance | 0}m ${t(
                "and-take-the",
              )} ${t(levelChangerType)} ${t(direction)} ${t("to")} ${
                destinationFloor?.name
                  ? getFloorName({
                      floor: destinationFloor,
                      language: currentLanguage,
                    })
                  : step.destinationLevel
              } ${t("floor").toLowerCase()}.`;
            }

            return {
              ...step,
              totalDistance,
              description,
              destinationFloor,
            };
          }),
      );
    }
  }, [
    haveRouteDetails,
    routeDetails?.TBTNav?.steps,
    currentFloor,
    currentLanguage,
    floors,
  ]);

  const startOverHandler = () => {
    setCurrentStep(0);
    resetAnimatedSteps();
    setTimeout(() => {
      setStepAnimated(0);
    }, 2000);

    map.setNavStep(0);
    if (steps.length === 1 || currentStep === 0) {
      map.restartRouteAnimation({ delay: 0 });
    }

    //}
  };

  if (mobileMode) {
    return (
      <>
        <div className="my-4 flex items-center gap-2">
          <p
            className={cn(
              "flex flex-1 items-center gap-4 rounded-lg bg-gray-50 p-4 text-sm",
              steps.length - 1 === currentStep && "opacity-1",
            )}
          >
            <span className="text-xl text-primary">
              {steps[currentStep]?.direction === "FINISH" ? (
                <FaFlag />
              ) : (
                <FaPersonWalking />
              )}
            </span>
            <span>{steps[currentStep]?.description}</span>
          </p>

          <Button
            className="h-10 w-10 rounded-full p-0"
            onClick={startOverHandler}
          >
            <FaArrowRotateRight className="text-lg" />
          </Button>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="py-5">
        <div className="flex">
          <ul
            className={cn(
              "flex flex-col gap-2 w-full",
            )}
          >
            {steps.slice(currentStep, currentStep + 2).map((step, index) => (
              <li
                key={index}
                className={cn(
                  "flex items-center gap-4 rounded-lg bg-gray-100 px-4 py-2",
                  index === 0 ? "" : "opacity-25",
                  steps.length - 1 === currentStep &&
                    "opacity-1 bg-black/85 text-white",
                )}
              >
                <span className="text-xl text-primary">
                  {step.direction === "FINISH" ? (
                    <FaFlag />
                  ) : (
                    <FaPersonWalking />
                  )}
                </span>
                <span>{step.description}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="mt-4 text-center">
          <Button
            size="lg"
            className="flex w-full gap-0.5"
            onClick={startOverHandler}
          >
            <FaArrowRotateRight className="text-lg group-data-[dir=ltr]:mr-2 group-data-[dir=rtl]:ml-2" />
            {t("replay")}
          </Button>
        </div>
      </div>
    </>
  );
}

export default PoiDetailsSteps;
